import { useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, User, Loader2, Eye, EyeOff, Check, X, AlertCircle } from 'lucide-react';
import { trackSignupConversion, trackEvent } from '@/lib/gtag';
import { getAnalyticsContext, getStoredUtm } from '@/lib/utm';

interface EmailAuthFormProps {
  onSuccess: () => void;
  mode?: 'signup' | 'login';
  onSwitchMode?: () => void;
}

type InlineError =
  | { kind: 'exists' }
  | { kind: 'invalid_credentials' }
  | { kind: 'rate_limited' }
  | { kind: 'generic'; message: string }
  | null;

export function EmailAuthForm({ onSuccess, mode = 'signup', onSwitchMode }: EmailAuthFormProps) {
  const isLogin = mode === 'login';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [inlineError, setInlineError] = useState<InlineError>(null);

  const passwordChecks = useMemo(() => ({
    length: password.length >= 8,
    letter: /[a-zA-Z]/.test(password),
    number: /\d/.test(password),
  }), [password]);
  const passwordOk = passwordChecks.length && passwordChecks.letter && passwordChecks.number;

  const emailOk = /\S+@\S+\.\S+/.test(email);
  const nameOk = name.trim().length >= 2;

  const canSubmit = isLogin
    ? emailOk && password.length > 0
    : emailOk && passwordOk && nameOk;

  const handleReset = async () => {
    if (!emailOk) {
      toast.error('Digite seu e-mail para recuperar a senha');
      return;
    }
    setResetting(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?mode=login`,
      });
      if (error) throw error;
      toast.success('Enviamos um link para redefinir sua senha. Verifique seu e-mail.');
    } catch (e) {
      console.error(e);
      toast.error('Não conseguimos enviar o e-mail agora. Tente novamente em instantes.');
    } finally {
      setResetting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInlineError(null);
    setLoading(true);

    trackEvent(isLogin ? 'login_started' : 'signup_started', {
      ...getAnalyticsContext(),
      method: 'email',
    });

    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          const msg = error.message.toLowerCase();
          if (msg.includes('invalid login credentials')) {
            setInlineError({ kind: 'invalid_credentials' });
          } else if (msg.includes('rate') || msg.includes('too many')) {
            setInlineError({ kind: 'rate_limited' });
          } else {
            setInlineError({ kind: 'generic', message: 'Não conseguimos entrar. Tente novamente.' });
          }
          trackEvent('login_error', { code: msg.slice(0, 60) });
          setLoading(false);
          return;
        }
        toast.success('Login realizado!');
        onSuccess();
      } else {
        const utm = getStoredUtm();
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/identify`,
            data: {
              full_name: name.trim(),
              ...utm,
            },
          },
        });

        if (error) {
          const msg = error.message.toLowerCase();
          if (msg.includes('already registered') || msg.includes('already been registered') || msg.includes('user already')) {
            setInlineError({ kind: 'exists' });
          } else if (msg.includes('rate') || msg.includes('over_email_send_rate_limit')) {
            setInlineError({ kind: 'rate_limited' });
          } else if (msg.includes('password')) {
            setInlineError({ kind: 'generic', message: 'Sua senha não atende aos requisitos. Use pelo menos 8 caracteres com letras e números.' });
          } else {
            setInlineError({ kind: 'generic', message: 'Não conseguimos criar sua conta agora. Tente novamente.' });
          }
          trackEvent('signup_error', { code: msg.slice(0, 60) });
          setLoading(false);
          return;
        }

        trackSignupConversion();
        trackEvent('signup_completed', { ...getAnalyticsContext(), method: 'email' });
        toast.success('Conta criada com sucesso!');
        onSuccess();
      }
    } catch (error) {
      console.error('Auth error:', error);
      setInlineError({ kind: 'generic', message: 'Ocorreu um erro. Tente novamente.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!isLogin && (
        <div className="space-y-1.5">
          <Label htmlFor="name" className="text-base text-foreground">Seu nome</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden />
            <Input
              id="name"
              type="text"
              autoComplete="name"
              placeholder="Como você quer ser chamado(a)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="pl-10 h-12 text-base"
              maxLength={60}
              required
            />
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="email" className="text-base text-foreground">E-mail</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden />
          <Input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="seu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value.trim())}
            className="pl-10 h-12 text-base"
            required
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor="password" className="text-base text-foreground">Senha</Label>
          {isLogin && (
            <button
              type="button"
              onClick={handleReset}
              className="text-sm text-primary hover:underline min-h-6"
              disabled={resetting}
            >
              {resetting ? 'Enviando…' : 'Esqueci minha senha'}
            </button>
          )}
        </div>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden />
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete={isLogin ? 'current-password' : 'new-password'}
            placeholder={isLogin ? 'Sua senha' : 'Crie uma senha'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pl-10 pr-11 h-12 text-base"
            required
            minLength={isLogin ? 1 : 8}
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-muted-foreground hover:text-foreground"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>

        {!isLogin && (
          <ul className="mt-2 space-y-1 text-sm" aria-live="polite">
            <PwCheck ok={passwordChecks.length} label="Pelo menos 8 caracteres" />
            <PwCheck ok={passwordChecks.letter} label="Contém letras" />
            <PwCheck ok={passwordChecks.number} label="Contém números" />
          </ul>
        )}
      </div>

      {inlineError && (
        <div
          role="alert"
          className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm text-foreground"
        >
          <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" aria-hidden />
          <div className="flex-1">
            {inlineError.kind === 'exists' && (
              <>
                <p>Este e-mail já tem uma conta na VerdeIA.</p>
                <div className="mt-2 flex gap-2 flex-wrap">
                  {onSwitchMode && (
                    <Button type="button" size="sm" variant="secondary" onClick={onSwitchMode} className="h-9">
                      Fazer login
                    </Button>
                  )}
                  <Button type="button" size="sm" variant="ghost" onClick={handleReset} className="h-9" disabled={resetting}>
                    Recuperar senha
                  </Button>
                </div>
              </>
            )}
            {inlineError.kind === 'invalid_credentials' && (
              <>
                <p>E-mail ou senha não conferem.</p>
                <div className="mt-2">
                  <Button type="button" size="sm" variant="ghost" onClick={handleReset} className="h-9" disabled={resetting}>
                    Esqueci minha senha
                  </Button>
                </div>
              </>
            )}
            {inlineError.kind === 'rate_limited' && (
              <p>Muitas tentativas em pouco tempo. Aguarde 1 minuto e tente novamente.</p>
            )}
            {inlineError.kind === 'generic' && <p>{inlineError.message}</p>}
          </div>
        </div>
      )}

      <Button
        type="submit"
        className="w-full h-12 gradient-nature text-primary-foreground text-base font-semibold press-scale"
        disabled={loading || !canSubmit}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            {isLogin ? 'Entrando…' : 'Criando sua conta…'}
          </>
        ) : isLogin ? (
          'Entrar'
        ) : (
          'Criar minha conta grátis'
        )}
      </Button>
    </form>
  );
}

function PwCheck({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className={`flex items-center gap-2 ${ok ? 'text-primary' : 'text-muted-foreground'}`}>
      {ok ? <Check className="h-3.5 w-3.5" aria-hidden /> : <X className="h-3.5 w-3.5" aria-hidden />}
      <span>{label}</span>
    </li>
  );
}
